// What the pointer is over, in words (plan C.2 `fast/tooltip.ts`, the port
// of `main.ts:1361-1367` and `:1373-1607`).
//
// The peek is DOM, not canvas (ui-map-interface.md, the canvas/DOM
// split): this module decides WHAT is under the pointer and hands it to
// `Overlay.showObjectPeek`, which writes the peek of the object's card
// with the card builder (R1810), or a small spec to `Overlay.showPeek` for
// open water, a ridge and a firing solution. It holds no element.
//
// It is event-driven and never on a timer (§10.3). A pointer move calls
// `moved`; the data arrivals that can change what a still cursor is over —
// a state frame, a cell flip, a site change — call `refreshAtCursor`, so a
// sovereignty gauge under a stationary cursor never goes stale.
//
// Every sentence comes from `gen/strings.ts`. This module writes none.

import { HeatmapKind, TorpedoKind } from '../gen/proto/aquaphobia';
import type { SonarRing, Wreck } from '../gen/proto/aquaphobia';
import { heatMode, type GameState } from '../state';
import type { Overlay } from '../ui/overlay';
import { gaugeVital, notePeek, peekOf, type CardSpec, type Vital } from '../ui/cards/card';
import {
  cellContents, cellListPeek, denKey, focusPeek, itemCard, onlyItem, poiKey, withListPress, withMore, wreckKey,
  type CellContents, type CellItem,
} from '../ui/cards/cellpeek';
import { seaObjectsIn, type SeaObjectView } from '../ui/cards/seaobjects';
import { ridgePeek, waterDeepLines, waterDeepOf, type RidgeAt } from '../ui/cards/terrain';
import { heatCellPeek } from '../ui/cards/heatcell';
import { BLOCK_RIDGE, BLOCK_TRENCH, cellAt, cellCenter, factionName } from '../world';
import { CHART, PEEK_MS } from '../gen/presentation';
import { main as S } from '../gen/strings';
import type { Hit } from './hit';
import type { CommandSites } from './commands';
import type { HoverRing } from './layers/sonar';
import type { PeekAction } from './markers';
import type { WorkPress } from '../ui/cards/treasure';
import type { SmallMark } from './smallmarks';

/** The touch margin of a small mark, in screen px (R2851). */
const TOUCH_MARGIN_PX = CHART.pieces.wrecks.fig.touch_margin_px;

/** The object a small mark stands for, as an item of the cell list. */
function markItem(m: SmallMark): CellItem | null {
  if (m.kind === 'wreck' && m.wreck) return { kind: 'wreck', key: wreckKey(m.wreck.fleet), wreck: m.wreck, units: 0 };
  if (m.kind === 'poi' && m.poi) return { kind: 'poi', key: poiKey(m.poi.id), poi: m.poi };
  if (m.kind === 'den' && m.den) return { kind: 'den', key: denKey(m.den.den), den: m.den };
  return null;
}

/** What the peek needs of the fleet layer: where one hull is, and the
 *  reach of an own hull for the firing solution. */
export interface TooltipFleets {
  describe(fleetId: number): {
    own: boolean; x: number; y: number; torpedoRange: number; firingKind: number;
  } | null;
}

/** What the tooltip needs of the site layer. */
export interface TooltipSites extends CommandSites {
  hoverRing(
    siteId: number, rings: readonly SonarRing[], viewer: number,
  ): HoverRing | null;
}

/** What the tooltip needs of the sonar layer: the hovered base's ring and
 *  the hovered fleet, whose ring draws brighter (R2210). */
export interface TooltipSonar {
  hoverRing: HoverRing | null;
  hoverFleetId: number;
}

/** What the tooltip needs of the chart: whether the pointer is on it. */
export interface TooltipMinimap {
  hit(px: number, py: number): { x: number; y: number } | null;
}

export interface TooltipDeps {
  ui: () => Overlay;
  state: GameState;
  fleets: TooltipFleets;
  /** The site layer, rebuilt by every hello, so it is read and not held. */
  sites: () => TooltipSites | null;
  sonar: TooltipSonar;
  minimap: TooltipMinimap;
  /** The owner byte of every cell. */
  owners: () => Uint8Array;
  /** The blocked mask, or null before the first hello. */
  blocked: () => Uint8Array | null;
  contestByCell: ReadonlyMap<number, { challenger: number; gauge: number }>;
  gaugeByCell: ReadonlyMap<number, number>;
  baseCells: ReadonlySet<number>;
  structureCells: ReadonlySet<number>;
  /** CSS px to world px, without the shake. */
  toWorld: (cx: number, cy: number, out: { x: number; y: number }) => { x: number; y: number };
  /** World px to CSS px, without the shake: where the peek's leader starts. */
  toScreen: (x: number, y: number, out: { x: number; y: number }) => { x: number; y: number };
  /** What the pointer landed on. `Commands.hitTest` answers it. */
  hitTest: (cx: number, cy: number) => Hit | null;
  /**
   * The map canvas's cursor, as the HOVER means it: 'pointer' over something
   * pickable, 'default' over open water. `Commands.setMapCursor` resolves it
   * — while a pick mode or the aim is armed the pointer goes away entirely
   * and the drawn crosshair stands in for it (R469, R500).
   */
  setCursor: (over: 'default' | 'pointer') => void;
  /**
   * The one action a pinned peek offers at a screen point (R134, R1294):
   * MARK, or REMOVE over an own map marker. `MarkerInput.peek` answers it.
   * A page that wires nothing pins a peek with no button.
   */
  peekAction?: (cx: number, cy: number) => PeekAction | null;
  /**
   * The work press of a pinned peek of water at a world point (v2 §9,
   * R2629, R2649): SEARCH HERE, at the point of `wreck` when it holds
   * cargo (R2860), while an own fleet is selected and `treasure` holds, and SEARCH with its
   * hull picker while none is (R2699). `wreck` is the wreck the peek
   * shows, or null for every other peek (R2851). It stands beside MARK. A
   * page that wires nothing pins a peek with MARK alone.
   */
  peekWork?: (wx: number, wy: number, wreck: Wreck | null) => WorkPress | null;
  /**
   * The ridge that holds a cell, with its chart name (§17.3.18), or null.
   * The chart furniture answers it; a page with no chart wires nothing,
   * and the ridge peek then draws the plain word and no elevation.
   */
  ridge?: (cell: number) => RidgeAt | null;
  /** The chart reading is historical: the elevation draws a hill. */
  historical?: () => boolean;
  /**
   * The small mark whose drawn box, grown by `marginPx` screen px, holds a
   * world point, or null (R2851): a wreck, a point of interest or a pirate
   * den, the nearest anchor first. The answer is one reused object. A
   * page with no small marks wires nothing.
   */
  mark?: (wx: number, wy: number, marginPx: number) => SmallMark | null;
  /**
   * The heat field the map paints (R1825): the mode, NONE while none
   * paints, and its field, null until the server sent one. The heatmap
   * layer answers it.
   */
  heat?: () => { mode: HeatmapKind; field: Uint8Array | null };
  /** Outlines the hovered cell of a peek, dashed, or none with −1
   *  (R1875). The hover cell layer answers it. */
  cellRing?: (cell: number) => void;
  /**
   * The sea objects in the viewer's picture (`State.objects`, v2 §11.9,
   * R2654), the rows of the store that holds them, with the life of an
   * own sea mine counted down. The water peek of a cell that holds one
   * shows its card (R2658). A page with no sea objects wires nothing.
   */
  seaObjects?: () => readonly SeaObjectView[];
  /**
   * The peek of a mark of the chart furniture at a screen point that a
   * long press of a finger peeks, or null: a shipping lane of the faction
   * (round 088 card 09, R2658). A click of a mouse on such a mark opens
   * its card through `Commands.pressMark` and never reaches the water peek.
   */
  markPeek?: (cx: number, cy: number) => CardSpec | null;
}

export class Tooltip {
  /**
   * Where the cursor last was over the canvas, so a data arrival can
   * re-render under a still cursor. Null when the pointer is off the map.
   * It is `hoverPt` whenever it is set: one reused point, so a pointer
   * move allocates nothing (R131).
   */
  private hoverAt: { x: number; y: number } | null = null;
  private readonly hoverPt = { x: 0, y: 0 };

  /**
   * A pinned peek (touch): while this is in the future the tooltip stays up
   * with no pointer holding it, and the `pointerleave` a lift raises must
   * not wipe it. Zero on a desktop, always — the mouse hovers for itself.
   */
  private peekUntil = 0;

  /** The action of the pinned peek, or null (R1294). */
  private peekAct: PeekAction | null = null;

  /** The work press of the pinned peek, or null (R2649). */
  private workAct: WorkPress | null = null;

  /**
   * What the last hit test found, and the cell the pointer stands on
   * (R548).
   *
   * The chart furniture reads both: a piece that answers a hover — the
   * outline of a ridge, the limit line — needs to know WHAT
   * is under the pointer, and the tooltip is the one place the hit test
   * already runs. The cell is −1 when the pointer is off the map.
   */
  private lastHitV: Hit | null = null;
  private hoverCellV = -1;

  /**
   * The id of the point of interest whose card the peek shows, 0 for none
   * (R2955). The chart furniture reads it: card 69 outlines the area of a
   * current gate while the peek shows the card of the gate. A point is a
   * small mark and never a hit, so `lastHit` cannot name it.
   */
  private peekPoiV = 0;

  /**
   * The world point of a click on open water, or null. Open water peeks on
   * a click and never on a hover: the peek, its cell outline and its leader
   * stand until the next press or a loss of focus. A hull, a site and the
   * firing solution still peek on a hover.
   */
  private waterPin: { x: number; y: number } | null = null;

  /**
   * The water peek lists every object of its cell (v2 §15.6, R2658,
   * R2851): a long press, or a second click on the cell of a desktop peek.
   * A press (a tap, a click) on a small mark peeks that mark, and a press
   * on the water of a cell of sea objects peeks the nearest of them.
   */
  private objList = false;

  /** The key of the object of the cell list a press picked to show alone,
   *  '' for the list (R2708). A new peek starts at the list. */
  private objFocus = '';

  /** The page of the cell list (R2851). A new peek starts at page 0. */
  private objPage = 0;

  /** The cell the pinned peek names, −1 for none (R2851). */
  private peekCell = -1;

  /** The key of the object the peek shows, '' for the water (R2851). */
  private shownKey = '';

  /** The pinned peek may carry a work press, and the key of the object
   *  it was asked for, null for none asked (R2649, R2851). */
  private workOn = false;
  private workKey: string | null = null;

  /** What the cell of the peek holds, kept until a data arrival. */
  private memo: { cell: number; c: CellContents } | null = null;

  private readonly pt = { x: 0, y: 0 };

  /** The hovered cell of the peek of this refresh, −1 for none. */
  private ringWant = -1;

  constructor(private readonly d: TooltipDeps) {}

  /** What the pointer is over, as the last refresh found it. */
  get lastHit(): Hit | null { return this.lastHitV; }

  /** The cell under the pointer, or −1 when the pointer is off the map. */
  get hoverCell(): number { return this.hoverCellV; }

  /** The point of interest whose card the peek shows, or 0 (R2955). */
  get peekPoi(): number { return this.peekPoiV; }

  /**
   * Where the pointer is over the canvas, in screen px, or null when it is
   * off the map. The crosshair draws at this point, because the hover is the
   * one thing that knows where the pointer is (R469, R500).
   */
  get hover(): { x: number; y: number } | null { return this.hoverAt; }

  // -- the pointer's own events ---------------------------------------------

  /** A mouse moved over the canvas. */
  moved(cx: number, cy: number): void {
    this.setHover(cx, cy);
    this.refresh(cx, cy);
  }

  /**
   * The pointer crossed off the canvas. The tooltip lives off canvas
   * `pointermove`, so the moment the pointer reaches a DOM panel the canvas
   * goes silent and the last tooltip would freeze on screen.
   */
  leave(): void {
    // A touch lift raises `pointerleave` too, and a peek is pinned precisely
    // because the finger that asked for it is gone.
    if (this.peekUntil > performance.now()) return;
    // The water peek of a click stands until a press or a loss of focus, so
    // the mouse can leave the canvas for its MARK (R2002).
    if (this.waterPin) return;
    this.drop();
  }

  /** A new peek: the cell list on or off, at its first page, with no row
   *  shown alone (R2708, R2851). */
  private startList(list: boolean): void {
    this.objList = list;
    this.objFocus = '';
    this.objPage = 0;
  }

  /** Puts the hover point at a screen point, in the one reused object. */
  private setHover(x: number, y: number): void {
    this.hoverPt.x = x;
    this.hoverPt.y = y;
    this.hoverAt = this.hoverPt;
  }

  /** Forgets the pointer: the tooltip goes, and the hover ring follows it. */
  private drop(): void {
    this.waterPin = null;
    this.objFocus = '';
    this.objPage = 0;
    this.peekCell = -1;
    this.workOn = false;
    this.memo = null;
    this.setPeekAction(null);
    this.hoverAt = null;
    this.lastHitV = null;
    this.hoverCellV = -1;
    this.peekPoiV = 0;
    this.d.sonar.hoverRing = null;
    this.d.sonar.hoverFleetId = 0;
    this.d.cellRing?.(-1);
    this.d.ui().hideTooltip();
  }

  /** A mouse press: the peek of a click on open water goes. Answers true
   *  when a peek stood, so the click of that press opens no new one. */
  dismissWaterPin(cx?: number, cy?: number): boolean {
    if (!this.waterPin) return false;
    // A second click on the cell of the peek lists the objects of the
    // cell, and the peek stays (v2 §15.6, R2658, R2851).
    if (cx !== undefined && cy !== undefined && this.listsOnSecondClick(cx, cy)) {
      this.startList(true);
      this.refreshAtCursor();
      return true;
    }
    this.waterPin = null;
    if (!this.pinned()) this.setPeekAction(null);
    this.refreshAtCursor();
    return true;
  }

  /** A press off the map, or a loss of focus: the water peek of a click
   *  goes, and the pointer it stood for is off the canvas (R2002). */
  dropWaterPin(): void {
    if (this.waterPin) this.drop();
  }

  /** Is a screen point open water: no minimap, no armed aim and no hit? */
  waterAt(cx: number, cy: number): boolean {
    const d = this.d;
    return !d.minimap.hit(cx, cy) && d.ui().aiming === null && !d.hitTest(cx, cy);
  }

  /**
   * A mouse click at a screen point: over open water it puts the water peek
   * up, and over anything else it puts none up.
   */
  clickWater(cx: number, cy: number): void {
    const d = this.d;
    this.waterPin = null;
    if (!this.waterAt(cx, cy)) return;
    const at = d.toWorld(cx, cy, this.pt);
    this.waterPin = { x: at.x, y: at.y };
    this.startList(false);
    this.memo = null;
    this.setHover(cx, cy);
    // The water peek of a click carries MARK, or REMOVE over an own map
    // marker, as the pinned peek of a finger does (R2002). The work press
    // follows what the peek shows (`show`).
    this.setPeekAction(d.peekAction?.(cx, cy) ?? null);
    this.workOn = true;
    this.workKey = null;
    this.refresh(cx, cy);
  }

  /** Re-renders under the last known cursor, for a data arrival. */
  refreshAtCursor(): void {
    this.memo = null;
    if (this.hoverAt) this.refresh(this.hoverAt.x, this.hoverAt.y);
  }

  /** Puts the tooltip up at a screen point and pins it (the touch peek). */
  pin(cx: number, cy: number, list = true): void {
    // A long press lists the objects of the cell (§15.6, R2851).
    this.startList(list);
    this.memo = null;
    this.setHover(cx, cy);
    this.peekUntil = performance.now() + PEEK_MS;
    // The pinned peek carries MARK, or REMOVE over an own map marker
    // (R1294). A peek that pins a firing solution carries neither: the
    // armed aim owns the next tap.
    const free = this.d.ui().aiming === null;
    this.setPeekAction(free ? this.d.peekAction?.(cx, cy) ?? null : null);
    // The work press stands on a peek of water alone (R2649): a hull or a
    // site under the finger keeps MARK. It follows what the peek shows.
    this.workOn = free && this.waterAt(cx, cy);
    this.workKey = null;
    if (!this.workOn) this.setWorkPress(null);
    this.refresh(cx, cy);
  }

  /**
   * A tap of a finger on open water (v2 §15.6, R2658, R2851): on a small
   * mark, in its drawn box and the touch margin, it pins the peek of that
   * mark; on the water of a cell that holds objects, it pins the peek of
   * the nearest sea object, or the peek of the cell. Answers false, and
   * pins nothing, on the water of a cell that holds nothing.
   */
  pickObject(cx: number, cy: number): boolean {
    if (!this.waterAt(cx, cy)) return false;
    const at = this.d.toWorld(cx, cy, this.pt);
    const wx = at.x, wy = at.y;
    if (!this.d.mark?.(wx, wy, TOUCH_MARGIN_PX)) {
      this.memo = null;
      if (this.contents(cellAt(wx, wy), wx, wy).total === 0) return false;
    }
    this.pin(cx, cy, false);
    return true;
  }

  /**
   * A press on a row of the cell list (R2708): the peek shows that object
   * alone, as the peek of a cell that holds one object does; the key ''
   * shows the list again. A pinned peek of a finger gets its full time
   * again, so the card stands while the finger reads it.
   */
  focusObject(key: string): void {
    // The "more" press of a peek (key '') opens the list, as a second
    // click does.
    if (!this.objList) {
      if (key !== '') return;
      this.objList = true;
      this.objPage = 0;
    }
    this.objFocus = key;
    if (this.pinned()) this.peekUntil = performance.now() + PEEK_MS;
    this.refreshAtCursor();
  }

  /** A press on the pager of the cell list (R2851): the list shows that
   *  page. A pinned peek of a finger gets its full time again. */
  pageObjects(page: number): void {
    if (!this.objList) return;
    this.objFocus = '';
    this.objPage = Math.max(0, page);
    if (this.pinned()) this.peekUntil = performance.now() + PEEK_MS;
    this.refreshAtCursor();
  }

  /** Does the cell at a screen point hold sea objects of the picture? A
   *  shipping lane under the point yields to them (`Commands.markAt`). */
  objectsAt(cx: number, cy: number): boolean {
    const at = this.d.toWorld(cx, cy, this.pt);
    return this.seaItems(cellAt(at.x, at.y), at.x, at.y).length > 0;
  }

  /** The sea objects of a cell, the nearest to a world point first. */
  private seaItems(cell: number, x: number, y: number): SeaObjectView[] {
    const rows = this.d.seaObjects?.();
    return rows && rows.length > 0 ? seaObjectsIn(rows, cell, x, y) : [];
  }

  /**
   * Does a click at this point list the objects of the water peek? The
   * click falls in the cell of the peek (the cell of a small mark under
   * it counts), and the cell holds two objects or more, or one that the
   * peek does not show yet (R2851).
   */
  private listsOnSecondClick(cx: number, cy: number): boolean {
    const pin = this.waterPin;
    if (!pin || this.objList || this.peekCell < 0) return false;
    const at = this.d.toWorld(cx, cy, this.pt);
    const wx = at.x, wy = at.y;
    const m = this.d.mark?.(wx, wy, 0) ?? null;
    if ((m ? cellAt(m.x, m.y) : cellAt(wx, wy)) !== this.peekCell) return false;
    const n = this.contents(this.peekCell, pin.x, pin.y).total;
    return n > 1 || (n === 1 && this.shownKey === '');
  }

  /** The pinned peek's action, and the label the tooltip draws for it. */
  private setPeekAction(a: PeekAction | null): void {
    this.peekAct = a;
    this.d.ui().setTooltipAction?.(a ? a.label : null);
    // The work press goes with the peek that carried it.
    if (!a) this.setWorkPress(null);
  }

  /** The pinned peek's work press, and the word the tooltip draws for it. */
  private setWorkPress(w: WorkPress | null): void {
    if (w === null) this.workKey = null;
    if (w === null && this.workAct === null) return;
    this.workAct = w;
    this.d.ui().setTooltipWork?.(w ? { label: w.label, title: w.title } : null);
  }

  /** A press on the peek's work press (R2649): the peek goes first, as
   *  it does for MARK, and the order goes out. */
  runPeekWork(): void {
    const w = this.workAct;
    this.dismissAll();
    w?.run();
  }

  /**
   * A press on the peek's button (R1294, R2002). The peek goes first, the
   * pinned peek of a finger and the water peek of a click alike, so the
   * menu that MARK opens stands alone on the screen.
   */
  runPeekAction(): void {
    const a = this.peekAct;
    this.dismissAll();
    a?.run();
  }

  /** Takes every peek away: a pinned one, the water peek of a click and
   *  the peek of a hover. The VIEW panel takes it on its opening, so no
   *  peek stands over the panel. */
  dismissAll(): void {
    this.peekUntil = 0;
    this.drop();
  }

  /** Takes a pinned peek away, whatever put it up. */
  dismissPeek(): void {
    if (this.peekUntil !== 0) this.dismissAll();
  }

  /**
   * A pinned peek has no pointer holding it up, so its own clock is the
   * only thing that can take it away. One number compare a frame.
   */
  tickPeek(): void {
    if (this.peekUntil > 0 && performance.now() > this.peekUntil) this.dismissPeek();
  }

  // -- the words -------------------------------------------------------------

  /** A pinned peek stands clear of the finger that pinned it (084 card 02). */
  private pinned(): boolean {
    return this.peekUntil > performance.now();
  }

  /** The screen point of a world point, in viewport px. */
  private screenOf(x: number, y: number): { x: number; y: number } {
    return this.d.toScreen(x, y, { x: 0, y: 0 });
  }

  /** Renders the peek for a screen point, and outlines the hovered cell
   *  while a peek of a map hover stands, or none (R1875). The minimap
   *  and the firing solution outline no cell. */
  refresh(cx: number, cy: number): void {
    this.ringWant = -1;
    this.peekAt(cx, cy);
    this.d.cellRing?.(this.ringWant);
  }

  private peekAt(cx: number, cy: number): void {
    const d = this.d;
    const ui = d.ui();
    const state = d.state;
    // Hovering a base lights what it can hear (§5, R106). It is recomputed
    // here because it is hit-test work, and this is the one place the hit
    // test already runs.
    d.sonar.hoverRing = null;
    d.sonar.hoverFleetId = 0;
    this.lastHitV = null;
    this.hoverCellV = -1;
    this.peekPoiV = 0;
    if (d.minimap.hit(cx, cy)) {
      ui.hideTooltip();
      // The chart is navigation and not a pick mode, so it keeps a real
      // cursor: after R469 a mark on this canvas means "an order is armed",
      // and a click that jumps the camera is not an order.
      d.setCursor('pointer');
      return;
    }
    // The cell under the pointer, for the chart furniture (R548). It is
    // read here because this is where the pointer's world point is already
    // computed, and a piece must not run a second hit test of its own.
    const at = d.toWorld(cx, cy, this.pt);
    this.hoverCellV = cellAt(at.x, at.y);
    const pointer = { x: cx, y: cy };
    if (ui.aiming !== null) {
      d.setCursor('default');
      // One lookup by id on every pointer move, and an own hull alone: the
      // hold's own battery is id 0, which names no hull.
      const shooter = d.fleets.describe(ui.aiming);
      if (shooter?.own) {
        const reach = Math.hypot(at.x - shooter.x, at.y - shooter.y);
        ui.showPeek(pointer, notePeek(S.aim_title, [
          reach <= shooter.torpedoRange
            ? S.aim_reach({ reach: Math.round(reach), range: shooter.torpedoRange })
            : S.aim_out_of_reach({ over: Math.round(reach - shooter.torpedoRange) }),
          shooter.firingKind === TorpedoKind.TORPEDO_KIND_SEEKER
            ? S.aim_seeker_hint
            : S.aim_dumb_hint,
        ]), null, this.pinned(), null, this.hoverCellV);
      } else if (ui.aiming === 0) {
        const round = state.torpedo(ui.baseTorp)?.name ?? S.base_round_default;
        const wait = Math.ceil(state.baseTubeReadySec());
        const free = state.baseTubesReady();
        ui.showPeek(pointer, notePeek(S.aim_title, [
          S.aim_base_round({ round: round.toLowerCase() }),
          // A tube is out for ten seconds after it shoots (R88), so the aim
          // needs to say whether there is one to shoot from.
          wait > 0 ? S.base_tubes_reloading({ sec: wait }) : S.base_tubes_ready({ free }),
          // A Remora from the hold locks the contact under the press (R2783).
          ...(ui.baseTorp === TorpedoKind.TORPEDO_KIND_SEEKER ? [S.aim_seeker_hint] : []),
        ]), null, this.pinned(), null, this.hoverCellV);
      }
      return;
    }
    const hit = d.hitTest(cx, cy);
    this.lastHitV = hit;
    if (!hit) { this.water(cx, cy); return; }
    d.setCursor('pointer');
    // A hover over an object ends the peek of a click on open water, and
    // its MARK with it.
    if (this.waterPin) {
      this.waterPin = null;
      if (!this.pinned()) this.setPeekAction(null);
    }
    if (hit.kind === 'fleet') {
      d.sonar.hoverFleetId = hit.id;
      this.fleet(cx, cy, hit.id);
    } else if (hit.kind === 'site') this.site(cx, cy, hit.id);
  }

  // -- open water ------------------------------------------------------------

  /**
   * Nothing under the cursor: the water itself is the information — whose it
   * is, and any capture gauge running on the cell (v5). A small mark of the
   * water (a wreck, a point of interest, a den) takes its own hover and its
   * own press (R2851).
   */
  private water(cx: number, cy: number): void {
    const d = this.d;
    const ui = d.ui();
    d.setCursor('default');
    const touch = this.pinned();
    const pin = touch ? null : this.waterPin;
    const at = pin ?? d.toWorld(cx, cy, this.pt);
    const wx = at.x, wy = at.y;
    // The pointer alone peeks at a small mark and at nothing else: a click
    // put the peek of the water up.
    if (!touch && !pin) { this.hoverMark(cx, cy, wx, wy); return; }
    if (!d.state.hello) { ui.hideTooltip(); return; }
    // A small mark under the press, in its drawn box; a finger adds the
    // touch margin (R2851). Its cell is the cell of the peek.
    const m = d.mark?.(wx, wy, touch ? TOUCH_MARGIN_PX : 0) ?? null;
    const item = m ? markItem(m) : null;
    const cell = m && item ? cellAt(m.x, m.y) : cellAt(wx, wy);
    if (cell < 0) { ui.hideTooltip(); return; }
    this.peekCell = cell;
    this.ringWant = cell;
    const pointer = pin ? this.screenOf(pin.x, pin.y) : { x: cx, y: cy };
    // The peek of the water has no mark, so its leader ties it to the
    // centre of the hovered cell (R1875).
    const [ccx, ccy] = cellCenter(cell);
    const mid = this.screenOf(ccx, ccy);
    // The cell list (v2 §15.6, R2658, R2708, R2851): a long press, or a
    // second click, lists every object of the cell. A cell of one object
    // peeks that object, as there is no list of one.
    if (this.objList) {
      const c = this.contents(cell, wx, wy);
      const one = onlyItem(c);
      const oneSpec = one ? itemCard(d.state, one) : null;
      if (one && oneSpec) {
        this.show(pointer, peekOf(oneSpec), null, touch, mid, cell, one, wx, wy);
        return;
      }
      if (c.total > 1) {
        // A row shows its object alone, with a press back to the list
        // (R2708); an object that left the cell shows the list again.
        const f = this.objFocus ? c.rows.find((r) => r.key === this.objFocus) : undefined;
        const fSpec = f ? itemCard(d.state, f) : null;
        if (f && fSpec) this.show(pointer, focusPeek(peekOf(fSpec), c.total), null, touch, mid, cell, f, wx, wy);
        else this.show(pointer, cellListPeek(d.state, c, this.objPage), null, touch, mid, cell, null, wx, wy);
        return;
      }
    }
    const spec = item ? itemCard(d.state, item) : null;
    if (m && item && spec) {
      const more = this.contents(cell, m.x, m.y).total - 1;
      this.show(pointer, withMore(peekOf(spec), more), this.screenOf(m.x, m.y), touch, null, cell,
        item, m.x, m.y);
      return;
    }
    const blocked = d.blocked();
    if (blocked && blocked[cell] === BLOCK_RIDGE) {
      // Rock belongs to nobody. The peek carries the elevation of the
      // ridge as its body (round 086 card 06 b), so no second card opens.
      // A seamount name carries the ⚑ of REPORT (R2633).
      const r = d.ridge?.(cell) ?? null;
      const mark = r?.seamount ? ui.seamountMark?.(r.seamount) ?? '' : '';
      this.show(pointer, r
        ? ridgePeek(r, cell, d.historical?.() ?? false, mark)
        : notePeek(S.ridge_title, [S.ridge_line]), null, touch, mid, cell, null, wx, wy);
      return;
    }
    // The sea objects of the cell (v2 §11.9, §15.6, round 088 card 13,
    // R2658): a press peeks the card of the nearest item, and its first
    // fact counts the rest of the cell.
    const c = this.contents(cell, wx, wy);
    const near = c.rows[0]?.kind === 'sea' ? c.rows[0] : null;
    const nearSpec = near ? itemCard(d.state, near) : null;
    if (near && nearSpec) {
      this.show(pointer, withMore(peekOf(nearSpec), c.total - 1), null, touch, mid, cell, near, wx, wy);
      return;
    }
    // A shipping lane of the faction under a long press (R2658).
    const lane = touch ? d.markPeek?.(cx, cy) ?? null : null;
    if (lane) {
      this.show(pointer, peekOf(lane), null, touch, mid, cell, null, wx, wy);
      return;
    }
    const own = d.owners()[cell];
    // While a heat mode paints, the peek of a cell reads the heat of that
    // cell (round 086 card 12, R1825); while none paints, it carries no
    // heat line. A mode with no field yet paints nothing, so the water
    // peek stands. CURRENTS and DEPTH have no server field: the water
    // peek states the depth and the current of the cell (R2870).
    const heat = d.heat?.();
    if (heat && heat.mode !== HeatmapKind.HEATMAP_KIND_NONE && !heatMode(heat.mode).local
      && heat.field && cell < heat.field.length) {
      this.show(pointer, heatCellPeek({
        mode: heat.mode, cell, value: heat.field[cell], owner: own, gaugeMax: d.state.rules.gaugeMax,
      }), null, touch, mid, cell, null, wx, wy);
      return;
    }
    const lines: string[] = [];
    lines.push(own === 0 ? S.water_unclaimed : S.water_sovereignty({ faction: factionName(own) }));
    if (blocked && blocked[cell] === BLOCK_TRENCH) lines.push(S.water_trench);
    // Water the survey of the faction does not hold (R2626). Every cell
    // reads surveyed while the wire carries no survey.
    if (!d.state.survey.has(cell)) lines.push(S.water_unsurveyed);
    // The depth and the current of the cell, each under its flag (R2706).
    else lines.push(...waterDeepLines(waterDeepOf(d.state), cell));
    const beam = d.contestByCell.get(cell);
    if (beam) lines.push(S.water_beam({ faction: factionName(beam.challenger), gauge: beam.gauge }));
    let gauge: Vital | undefined;
    if (d.structureCells.has(cell)) {
      // The ground a strategic object stands on is the object's: no beam may
      // work it and it changes hands only when the object does.
      lines.push(S.water_object_tile);
    } else if (own !== 0) {
      // The cell's gauge in the vital idiom against the cap the catalog
      // states (084 open point 8): no dial (round 084 card 01).
      const v = (d.gaugeByCell.get(cell) ?? 0) / 10;
      gauge = gaugeVital(v, d.state.rules.gaugeMax);
      if (d.baseCells.has(cell)) {
        // R66: a base cell is uncapturable, and R114's anchor push skips it
        // too — no promise of it changing hands.
        lines.push(S.water_base_cell);
      } else if (v <= 0) {
        // R126: a spent baseless tile releases to the sea on the next field
        // pass — owned-at-zero is a moment, not a state.
        lines.push(S.water_spent);
      }
    }
    // The water wears its owner's colour. The objects of the cell are a
    // press under the lines that lists them (R2851).
    const water = notePeek(own === 0 ? S.water_open_sea_title : S.water_territory_title, lines, own, gauge);
    this.show(pointer, c.total > 0 ? withListPress(water, S.water_marks({ n: c.total })) : water,
      null, touch, mid, cell, null, wx, wy);
  }

  /**
   * A mouse hover over the water (R2851): a small mark under the pointer,
   * in its drawn box, peeks its card beside the mark, as a hull does. Open
   * water peeks nothing.
   */
  private hoverMark(cx: number, cy: number, wx: number, wy: number): void {
    const d = this.d;
    const m = d.mark?.(wx, wy, 0) ?? null;
    const item = m ? markItem(m) : null;
    const spec = item ? itemCard(d.state, item) : null;
    if (!m || !spec) { d.ui().hideTooltip(); return; }
    d.setCursor('pointer');
    const cell = cellAt(m.x, m.y);
    this.ringWant = cell;
    if (item?.kind === 'poi') this.peekPoiV = item.poi.id;
    d.ui().showPeek({ x: cx, y: cy }, peekOf(spec), this.screenOf(m.x, m.y), false, null, cell);
  }

  /**
   * Puts a peek of the water up. The work press of a pinned peek follows
   * what it shows (R2649, R2851): a SEARCH at the point of a sighted wreck
   * on its card (R2860), at the cell on the rest. It is asked again only when the shown object
   * changes, and before the peek writes, because the peek draws it.
   */
  private show(
    pointer: { x: number; y: number }, spec: CardSpec, at: { x: number; y: number } | null, touch: boolean,
    mid: { x: number; y: number } | null, cell: number, item: CellItem | null, wx: number, wy: number,
  ): void {
    const key = item?.key ?? '';
    this.shownKey = key;
    this.peekPoiV = item?.kind === 'poi' ? item.poi.id : 0;
    if (this.workOn && key !== this.workKey) {
      const wreck = item?.kind === 'wreck' ? item.wreck : null;
      this.setWorkPress(this.d.peekWork?.(wx, wy, wreck) ?? null);
      this.workKey = key;
    }
    this.d.ui().showPeek(pointer, spec, at, touch, mid, cell);
  }

  /** What a cell holds, kept for one peek: a pointer move under a pinned
   *  peek reads it again, and a data arrival forgets it. */
  private contents(cell: number, x: number, y: number): CellContents {
    const memo = this.memo;
    if (memo && memo.cell === cell) return memo.c;
    const c = cellContents(this.d.state, cell, this.seaItems(cell, x, y));
    this.memo = { cell, c };
    return c;
  }

  // -- a hull ----------------------------------------------------------------

  /** A hull: the peek of its card, beside the hull (R1810). A hull moves
   *  and seldom stands on the centre of its cell, so its peek outlines no
   *  cell: the leader to the hull names it (R1875). */
  private fleet(cx: number, cy: number, id: number): void {
    const f = this.d.fleets.describe(id);
    const at = f ? this.screenOf(f.x, f.y) : null;
    this.d.ui().showObjectPeek({ x: cx, y: cy }, { fleet: id }, at, this.pinned());
  }

  // -- a site ----------------------------------------------------------------

  /** A site: the peek of its card, beside the site (R1810). */
  private site(cx: number, cy: number, id: number): void {
    const d = this.d;
    const state = d.state;
    const layer = d.sites();
    const s = state.sites.get(id) ?? layer?.get(id);
    if (!s) return;
    // The hit ring of a site reaches past its cell, so the outline names
    // the cell of the site and not the cell under the pointer.
    this.ringWant = s.cell;
    // A base under the cursor lights what it can hear (§5, R106). The site
    // layer decides whether there is an honest range to draw.
    d.sonar.hoverRing = layer?.hoverRing(
      id, state.empire?.sonarRings ?? [], state.faction) ?? null;
    const [wx, wy] = cellCenter(s.cell);
    // The reach we were actually given, never a guessed one (R112).
    d.ui().showObjectPeek({ x: cx, y: cy }, { site: s }, this.screenOf(wx, wy), this.pinned(),
      d.sonar.hoverRing?.range ?? 0);
  }
}
